// frontend/src/components/crm/CampaignBuilder.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { campaignsAPI, emailTemplateAPI, workflowAPI, audiencesAPI, sendingProfilesAPI } from '../../utils/crmApi';
import { Link } from 'react-router-dom';
import {
    UsersIcon,
    DocumentTextIcon,
    SparklesIcon,
    CogIcon,
    PlusIcon,
    TrashIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    ClockIcon,
    CheckCircleIcon,
    XCircleIcon,
    ArrowPathIcon,
    PlayIcon,
    PauseIcon,
    ExclamationTriangleIcon,
    ChartBarIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useBranding } from '../../contexts/BrandingContext';
import { useDialog, Dialog, DialogHeader, DialogBody, DialogFooter } from '../../components/kibo-ui/dialog';

const CampaignBuilder = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { branding } = useBranding();
    const { openDialog } = useDialog();
    const isEditing = !!id;
    
    const [activeTab, setActiveTab] = useState('audience');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    
    // Campaign data
    const [campaign, setCampaign] = useState({
        name: '',
        description: '',
        type: 'broadcast',
        status: 'draft',
        audienceId: null,
        steps: [],
        entryStepId: null,
        entryRule: null,
        scheduledAt: null,
        timezone: 'Europe/Madrid',
        sendingProfileId: null,
        utmParams: {
            source: '',
            medium: 'email',
            campaign: '',
            term: '',
            content: ''
        },
        settings: {
            trackOpens: true,
            trackClicks: true,
            allowUnsubscribe: true,
            plainTextFallback: true,
            linkTracking: true
        },
        suppressionLists: {
            globalUnsub: true
        },
        frequencyCap: {
            max: 2,
            windowDays: 7
        }
    });
    
    // Data for dropdowns
    const [templates, setTemplates] = useState([]);
    const [workflows, setWorkflows] = useState([]);
    const [audiences, setAudiences] = useState([]);
    const [sendingProfiles, setSendingProfiles] = useState([]);
    
    useEffect(() => {
        // Restore campaign state from sessionStorage if available (when returning from audience creation)
        const savedCampaign = sessionStorage.getItem('campaignBuilder_draft');
        const savedReturnTo = sessionStorage.getItem('campaignBuilder_returnTo');
        
        // Only restore if we're on the same page we saved from
        if (savedCampaign && savedReturnTo) {
            const currentPath = isEditing ? `/campaigns/${id}` : '/campaigns/new';
            if (savedReturnTo === currentPath) {
                try {
                    const parsed = JSON.parse(savedCampaign);
                    setCampaign(parsed);
                    sessionStorage.removeItem('campaignBuilder_draft'); // Clear after restoring
                    sessionStorage.removeItem('campaignBuilder_returnTo');
                } catch (e) {
                    console.error('Failed to restore campaign state:', e);
                }
            } else {
                // Clear stale data
                sessionStorage.removeItem('campaignBuilder_draft');
                sessionStorage.removeItem('campaignBuilder_returnTo');
            }
        }
        
        if (!savedCampaign && isEditing) {
            loadCampaign();
        }
        loadData();
    }, [id, isEditing]);
    
    // Save campaign state to sessionStorage before navigating away
    useEffect(() => {
        const handleBeforeUnload = () => {
            // Save if there's some data
            if (campaign.name || campaign.steps.length > 0 || campaign.audienceId) {
                sessionStorage.setItem('campaignBuilder_draft', JSON.stringify(campaign));
                sessionStorage.setItem('campaignBuilder_returnTo', isEditing ? `/campaigns/${id}` : '/campaigns/new');
            }
        };
        
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [campaign, isEditing, id]);
    
    const loadAudiences = async () => {
        try {
            const response = await audiencesAPI.getAll({ limit: 100 });
            if (response.data.success) {
                setAudiences(response.data.data || []);
            }
        } catch (error) {
            console.error('Failed to load audiences:', error);
        }
    };
    
    const loadCampaign = async () => {
        try {
            setLoading(true);
            const response = await campaignsAPI.getById(id);
            if (response.data.success) {
                const campaignData = response.data.data;
                // Ensure audienceId is a string (handle both populated and non-populated cases)
                if (campaignData.audienceId) {
                    campaignData.audienceId = typeof campaignData.audienceId === 'object' 
                        ? campaignData.audienceId._id || campaignData.audienceId.id 
                        : campaignData.audienceId;
                }
                
                // Normalize templateId in steps variants (handle populated objects)
                if (campaignData.steps && Array.isArray(campaignData.steps)) {
                    campaignData.steps = campaignData.steps.map(step => {
                        if (step.variants && Array.isArray(step.variants)) {
                            step.variants = step.variants.map(variant => {
                                if (variant.templateId) {
                                    // Convert ObjectId or populated object to string
                                    if (typeof variant.templateId === 'object') {
                                        variant.templateId = variant.templateId._id || variant.templateId.id || String(variant.templateId);
                                    } else {
                                        variant.templateId = String(variant.templateId);
                                    }
                                }
                                return variant;
                            });
                        }
                        return step;
                    });
                }
                
                // Ensure type has a default value if missing
                if (!campaignData.type) {
                    campaignData.type = 'broadcast';
                }
                
                setCampaign(campaignData);
            }
        } catch (error) {
            console.error('Failed to load campaign:', error);
            toast.error('Failed to load campaign');
        } finally {
            setLoading(false);
        }
    };
    
    const loadData = async () => {
        try {
            const [templatesRes, workflowsRes, audiencesRes, sendingProfilesRes] = await Promise.all([
                emailTemplateAPI.getAll(),
                workflowAPI.getAll(),
                audiencesAPI.getAll({ limit: 100 }),
                sendingProfilesAPI.getAll({ limit: 100 })
            ]);
            
            if (templatesRes.data.success) {
                setTemplates(templatesRes.data.data || []);
            }
            if (workflowsRes.data.success) {
                setWorkflows(workflowsRes.data.data || []);
            }
            if (audiencesRes.data.success) {
                setAudiences(audiencesRes.data.data || []);
            }
            if (sendingProfilesRes.data.success) {
                setSendingProfiles(sendingProfilesRes.data.data || []);
            }
        } catch (error) {
            console.error('Failed to load data:', error);
        }
    };
    
    const handleSave = async () => {
        if (!campaign.name) {
            toast.error('Campaign name is required');
            return;
        }
        
        // Prevent saving running or completed campaigns
        if (isEditing && ['running', 'completed'].includes(campaign.status)) {
            toast.error(`Cannot update ${campaign.status} campaigns. Please pause the campaign first to make changes.`);
            return;
        }
        
        try {
            setSaving(true);
            let response;
            if (isEditing) {
                response = await campaignsAPI.update(id, campaign);
            } else {
                response = await campaignsAPI.create(campaign);
            }
            
            if (response.data.success) {
                toast.success(`Campaign ${isEditing ? 'updated' : 'created'} successfully`);
                // Navigate back to campaigns list after saving
                navigate('/campaigns');
            }
        } catch (error) {
            console.error('Failed to save campaign:', error);
            const errorMessage = error.response?.data?.message || 'Failed to save campaign';
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };
    
    const handlePause = () => {
        if (!id) return;
        
        openDialog({
            variant: 'warning',
            title: 'Pause Campaign',
            description: 'Are you sure you want to pause this campaign? The campaign will stop sending emails, but you can resume it later.',
            confirmText: 'Pause Campaign',
            cancelText: 'Cancel',
            onConfirm: async () => {
                try {
                    setSaving(true);
                    const response = await campaignsAPI.pause(id);
                    
                    console.log('Pause response:', response); // Debug log
                    
                    if (response?.data?.success) {
                        toast.success(response.data.message || 'Campaign paused successfully');
                        // Update local campaign status
                        setCampaign(prev => ({ ...prev, status: 'paused' }));
                    } else {
                        toast.error(response?.data?.message || 'Failed to pause campaign');
                    }
                } catch (error) {
                    console.error('Failed to pause campaign:', error);
                    const errorMessage = error.response?.data?.message || error.message || 'Failed to pause campaign';
                    toast.error(errorMessage);
                } finally {
                    setSaving(false);
                }
            }
        });
    };
    
    const handleResume = () => {
        if (!id) return;
        
        openDialog({
            variant: 'info',
            title: 'Resume Campaign',
            description: 'Are you sure you want to resume this campaign? The campaign will continue sending emails from where it was paused.',
            confirmText: 'Resume Campaign',
            cancelText: 'Cancel',
            onConfirm: async () => {
                try {
                    setSaving(true);
                    const response = await campaignsAPI.resume(id);
                    
                    console.log('Resume response:', response); // Debug log
                    
                    if (response?.data?.success) {
                        toast.success(response.data.message || 'Campaign resumed successfully');
                        // Update local campaign status
                        setCampaign(prev => ({ ...prev, status: 'running' }));
                    } else {
                        toast.error(response?.data?.message || 'Failed to resume campaign');
                    }
                } catch (error) {
                    console.error('Failed to resume campaign:', error);
                    const errorMessage = error.response?.data?.message || error.message || 'Failed to resume campaign';
                    toast.error(errorMessage);
                } finally {
                    setSaving(false);
                }
            }
        });
    };
    
    const handleStart = async () => {
        // Validate required fields
        if (!campaign.name) {
            toast.error('Campaign name is required');
            return;
        }
        
        if (!campaign.audienceId) {
            toast.error('Please select an audience before starting the campaign');
            setActiveTab('audience');
            return;
        }
        
        if (campaign.type === 'broadcast' && (!campaign.steps || campaign.steps.filter(s => s.kind === 'send').length === 0)) {
            toast.error('Please add at least one email step before starting the campaign');
            setActiveTab('content');
            return;
        }
        
        // Find the selected audience to get contact count
        const selectedAudience = audiences.find(a => String(a._id || a.id) === String(campaign.audienceId));
        const contactCount = selectedAudience?.estimatedSize || 0;
        const audienceName = selectedAudience?.name || 'the selected audience';
        
        // Format the number with commas
        const formatNumber = (num) => {
            return num.toLocaleString('en-US');
        };
        
        openDialog({
            variant: 'warning',
            title: 'Start Campaign Now',
            description: `Are you sure you want to start this campaign immediately? The campaign will begin sending emails right away to ${formatNumber(contactCount)} contact${contactCount !== 1 ? 's' : ''} in "${audienceName}".`,
            confirmText: 'Start Now',
            cancelText: 'Cancel',
            onConfirm: async () => {
                try {
                    setSaving(true);
                    
                    // If this is a new campaign (not saved yet), save it first
                    let campaignId = id;
                    if (!isEditing || !campaignId) {
                        const createResponse = await campaignsAPI.create(campaign);
                        if (!createResponse.data.success) {
                            toast.error('Failed to create campaign');
                            return;
                        }
                        campaignId = createResponse.data.data._id;
                    }
                    
                    // Now start the campaign
                    const response = await campaignsAPI.start(campaignId);
                    
                    if (response.data.success) {
                        toast.success(response.data.message || 'Campaign started successfully');
                        navigate('/campaigns');
                    } else {
                        toast.error(response.data.message || 'Failed to start campaign');
                    }
                } catch (error) {
                    console.error('Failed to start campaign:', error);
                    const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to start campaign';
                    toast.error(errorMessage);
                    console.error('Error response:', error.response?.data);
                } finally {
                    setSaving(false);
                }
            }
        });
    };
    
    const handleSchedule = async () => {
        if (!campaign.scheduledAt) {
            toast.error('Please set a scheduled date and time in the Settings tab');
            // Switch to settings tab to show the schedule input
            setActiveTab('settings');
            return;
        }
        
        // Validate required fields
        if (!campaign.audienceId) {
            toast.error('Please select an audience before scheduling the campaign');
            setActiveTab('audience');
            return;
        }
        
        if (campaign.type === 'broadcast' && (!campaign.steps || campaign.steps.filter(s => s.kind === 'send').length === 0)) {
            toast.error('Please add at least one email step before scheduling the campaign');
            setActiveTab('content');
            return;
        }
        
        try {
            setSaving(true);
            const response = await campaignsAPI.schedule(id, {
                scheduledAt: campaign.scheduledAt,
                timezone: campaign.timezone
            });
            
            if (response.data.success) {
                toast.success(response.data.message || 'Campaign scheduled successfully');
                navigate('/campaigns');
            }
        } catch (error) {
            console.error('Failed to schedule campaign:', error);
            toast.error(error.response?.data?.message || 'Failed to schedule campaign');
        } finally {
            setSaving(false);
        }
    };

    const [testEmailsInput, setTestEmailsInput] = useState('');
    const [isSendingTest, setIsSendingTest] = useState(false);
    const [showTestEmailDialog, setShowTestEmailDialog] = useState(false);

    const handleTestSend = () => {
        // Check if campaign has at least one send step
        const hasSendStep = campaign.steps.some(step => step.kind === 'send');
        if (!hasSendStep) {
            toast.error('Please add at least one email step to test');
            setActiveTab('journey');
            return;
        }

        // Reset input and show dialog
        setTestEmailsInput('');
        setShowTestEmailDialog(true);
    };

    const handleSendTestEmail = async () => {
        if (!testEmailsInput || !testEmailsInput.trim()) {
            toast.error('Please enter at least one email address');
            return;
        }

        const emailList = testEmailsInput.split(',').map(email => email.trim()).filter(email => email);
        if (emailList.length === 0) {
            toast.error('Please enter at least one valid email address');
            return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const invalidEmails = emailList.filter(email => !emailRegex.test(email));
        if (invalidEmails.length > 0) {
            toast.error(`Invalid email addresses: ${invalidEmails.join(', ')}`);
            return;
        }

        try {
            setIsSendingTest(true);
            setSaving(true);
            const response = await campaignsAPI.testSend(id, {
                emails: emailList
            });
            
            if (response.data.success) {
                toast.success(`Test email sent to ${emailList.length} address(es)`);
                setShowTestEmailDialog(false);
            }
        } catch (error) {
            console.error('Failed to send test email:', error);
            toast.error(error.response?.data?.message || 'Failed to send test email');
        } finally {
            setIsSendingTest(false);
            setSaving(false);
        }
    };
    
    const addStep = (kind) => {
        const newStep = {
            id: `step-${Date.now()}`,
            kind,
            name: '',
            order: campaign.steps.length,
            ...(kind === 'send' && {
                variants: [{
                    id: `variant-${Date.now()}`,
                    name: 'Variant A',
                    percent: 100,
                    templateId: null,
                    subject: '',
                    preheader: ''
                }],
                tracking: {
                    utm: true,
                    pixel: true,
                    links: true
                }
            }),
            ...(kind === 'wait' && {
                waitRule: {
                    type: 'duration',
                    value: 'P1D'
                }
            }),
            ...(kind === 'branch' && {
                condition: {
                    type: 'opened',
                    operator: 'equals',
                    value: true
                },
                thenId: null,
                elseId: null
            }),
            ...(kind === 'goal' && {
                goal: {
                    event: 'order_placed',
                    exitOnHit: true
                }
            })
        };
        
        setCampaign(prev => ({
            ...prev,
            steps: [...prev.steps, newStep],
            entryStepId: prev.entryStepId || newStep.id
        }));
    };
    
    const updateStep = (stepId, updates) => {
        setCampaign(prev => ({
            ...prev,
            steps: prev.steps.map(step =>
                step.id === stepId ? { ...step, ...updates } : step
            )
        }));
    };
    
    const deleteStep = (stepId) => {
        setCampaign(prev => ({
            ...prev,
            steps: prev.steps.filter(step => step.id !== stepId),
            entryStepId: prev.entryStepId === stepId ? (prev.steps[0]?.id || null) : prev.entryStepId
        }));
    };
    
    const moveStep = (stepId, direction) => {
        setCampaign(prev => {
            const steps = [...prev.steps];
            const index = steps.findIndex(s => s.id === stepId);
            if (index === -1) return prev;
            
            const newIndex = direction === 'up' ? index - 1 : index + 1;
            if (newIndex < 0 || newIndex >= steps.length) return prev;
            
            [steps[index], steps[newIndex]] = [steps[newIndex], steps[index]];
            steps[index].order = index;
            steps[newIndex].order = newIndex;
            
            return { ...prev, steps };
        });
    };
    
    if (loading) {
        return (
            <div className="p-6">
                <div className="text-center py-12">Loading campaign...</div>
            </div>
        );
    }
    
    return (
        <div className="p-6">
            {/* Test Email Dialog */}
            {showTestEmailDialog && (
                <Dialog isOpen={showTestEmailDialog} onClose={() => setShowTestEmailDialog(false)} size="md">
                    <DialogHeader title="Send Test Email" onClose={() => setShowTestEmailDialog(false)} />
                    <DialogBody>
                        <label className="block text-sm font-medium text-foreground mb-2">
                            Enter test email addresses (comma-separated):
                        </label>
                        <input
                            type="text"
                            placeholder="email1@example.com, email2@example.com"
                            value={testEmailsInput}
                            onChange={(e) => setTestEmailsInput(e.target.value)}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !isSendingTest && testEmailsInput.trim()) {
                                    handleSendTestEmail();
                                }
                            }}
                        />
                        <p className="text-xs text-muted-foreground mt-2">
                            Separate multiple emails with commas
                        </p>
                    </DialogBody>
                    <DialogFooter>
                        <button
                            onClick={() => setShowTestEmailDialog(false)}
                            disabled={isSendingTest}
                            className="px-4 py-2 text-sm font-medium text-foreground bg-background border border-input rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSendTestEmail}
                            disabled={isSendingTest || !testEmailsInput.trim()}
                            className="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                            {isSendingTest ? 'Sending...' : 'Send'}
                        </button>
                    </DialogFooter>
                </Dialog>
            )}

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-foreground">
                        {isEditing ? 'Edit Campaign' : 'Create Campaign'}
                    </h1>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                        {isEditing ? 'Update your campaign settings and content' : 'Build your email campaign step by step'}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => navigate('/campaigns')}
                        className="px-3 sm:px-4 py-2 text-sm sm:text-base border border-input rounded-lg hover:bg-muted whitespace-nowrap"
                    >
                        Cancel
                    </button>
                    {isEditing && campaign.status === 'running' && (
                        <button
                            onClick={handlePause}
                            disabled={saving}
                            className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center gap-1 sm:gap-2 whitespace-nowrap"
                        >
                            <PauseIcon className="h-4 w-4" />
                            <span className="hidden sm:inline">Pause</span>
                        </button>
                    )}
                    {isEditing && campaign.status === 'paused' && (
                        <button
                            onClick={handleResume}
                            disabled={saving}
                            className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1 sm:gap-2 whitespace-nowrap"
                        >
                            <PlayIcon className="h-4 w-4" />
                            <span className="hidden sm:inline">Resume</span>
                        </button>
                    )}
                    {isEditing && (
                        <Link
                            to={`/campaigns/${id}/analytics`}
                            className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1 sm:gap-2 whitespace-nowrap"
                        >
                            <ChartBarIcon className="h-4 w-4" />
                            <span className="hidden sm:inline">Analytics</span>
                        </Link>
                    )}
                    {!['running', 'completed'].includes(campaign.status) && (
                        <>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 whitespace-nowrap"
                            >
                                {saving ? 'Saving...' : 'Save Draft'}
                            </button>
                            {(isEditing && campaign.status === 'draft') || (!isEditing && campaign.name) ? (
                                <>
                                    {isEditing && (
                                        <button
                                            onClick={handleTestSend}
                                            disabled={saving}
                                            className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1 sm:gap-2 whitespace-nowrap"
                                        >
                                            <PlayIcon className="h-4 w-4" />
                                            <span className="hidden sm:inline">Test Send</span>
                                        </button>
                                    )}
                                    <button
                                        onClick={handleStart}
                                        disabled={saving}
                                        className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1 sm:gap-2 whitespace-nowrap"
                                    >
                                        <PlayIcon className="h-4 w-4" />
                                        <span className="hidden sm:inline">Send Now</span>
                                    </button>
                                    <button
                                        onClick={handleSchedule}
                                        disabled={saving}
                                        className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1 sm:gap-2 whitespace-nowrap"
                                    >
                                        <ClockIcon className="h-4 w-4" />
                                        <span className="hidden sm:inline">Schedule</span>
                                    </button>
                                </>
                            ) : null}
                        </>
                    )}
                    {campaign.status === 'completed' && (
                        <span className="px-3 sm:px-4 py-2 text-xs sm:text-sm text-muted-foreground flex items-center gap-1 sm:gap-2 whitespace-nowrap">
                            <CheckCircleIcon className="h-4 w-4" />
                            <span className="hidden sm:inline">Campaign Completed</span>
                        </span>
                    )}
                </div>
            </div>
            
            {/* Status Warning for Running/Completed Campaigns */}
            {isEditing && ['running', 'completed'].includes(campaign.status) && (
                <div className="bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-3">
                        <ExclamationTriangleIcon className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <h3 className="text-sm font-semibold text-orange-900 dark:text-orange-100 mb-1">
                                {campaign.status === 'running' ? 'Campaign is Running' : 'Campaign is Completed'}
                            </h3>
                            <p className="text-sm text-orange-800 dark:text-orange-200">
                                {campaign.status === 'running' 
                                    ? 'This campaign is currently running. You cannot make changes while it\'s active. Please pause the campaign first to edit it.'
                                    : 'This campaign has been completed. You cannot make changes to completed campaigns.'}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Campaign Name & Type */}
            <div className={`bg-card border border-border rounded-lg p-6 mb-6 ${['running', 'completed'].includes(campaign.status) ? 'opacity-60 pointer-events-none' : ''}`}>
                <h2 className="text-lg font-semibold text-foreground mb-4">Campaign Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Campaign Name *
                        </label>
                        <input
                            type="text"
                            value={campaign.name}
                            onChange={(e) => setCampaign(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
                            placeholder="e.g., Welcome Email Series"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Campaign Type *
                        </label>
                        <select
                            value={campaign.type}
                            onChange={(e) => {
                                const newType = e.target.value;
                                // Warn user if changing type might affect existing steps
                                if (isEditing && campaign.steps.length > 0) {
                                    openDialog({
                                        variant: 'warning',
                                        title: 'Change Campaign Type',
                                        description: `Changing campaign type from ${campaign.type} to ${newType} may affect existing steps. Do you want to continue?`,
                                        confirmText: 'Continue',
                                        cancelText: 'Cancel',
                                        onConfirm: () => {
                                            setCampaign(prev => ({ ...prev, type: newType }));
                                        }
                                    });
                                    return;
                                }
                                setCampaign(prev => ({ ...prev, type: newType }));
                            }}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
                            disabled={isEditing && ['running', 'completed'].includes(campaign.status)}
                        >
                            <option value="broadcast">📢 Broadcast (One-time send)</option>
                            <option value="sequence">🔄 Sequence (Multi-step drip)</option>
                            <option value="journey">🎯 Journey (Event-triggered)</option>
                        </select>
                        <p className="text-xs text-muted-foreground mt-1">
                            {isEditing && ['running', 'completed'].includes(campaign.status)
                                ? 'Campaign type cannot be changed while running or after completion'
                                : campaign.type === 'broadcast' 
                                ? 'Send once to your audience'
                                : campaign.type === 'sequence'
                                ? 'Send multiple emails over time with delays'
                                : 'Triggered by workflow events'}
                        </p>
                    </div>
                </div>
                <div className="mt-4">
                    <label className="block text-sm font-medium text-foreground mb-1">
                        Description
                    </label>
                    <textarea
                        value={campaign.description}
                        onChange={(e) => setCampaign(prev => ({ ...prev, description: e.target.value }))}
                        className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
                        rows={2}
                        placeholder="Describe what this campaign does..."
                    />
                </div>
            </div>
            
            {/* Tabs */}
            <div className="bg-card border border-border rounded-lg">
                <div className="border-b border-border">
                    <nav className="flex space-x-8 px-6">
                        {[
                            { id: 'audience', label: 'Audience', icon: UsersIcon },
                            { id: 'content', label: 'Content', icon: DocumentTextIcon },
                            ...(campaign.type !== 'broadcast' ? [{ id: 'journey', label: 'Journey', icon: SparklesIcon }] : []),
                            { id: 'settings', label: 'Settings', icon: CogIcon }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                                    activeTab === tab.id
                                        ? 'border-primary text-primary'
                                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-input'
                                }`}
                            >
                                <tab.icon className="h-5 w-5" />
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>
                
                {/* Tab Content */}
                <div className="p-6">
                    {activeTab === 'audience' && (
                        <AudienceTab
                            campaign={campaign}
                            setCampaign={setCampaign}
                            audiences={audiences}
                            isEditing={isEditing}
                            id={id}
                        />
                    )}
                    
                    {activeTab === 'content' && (
                        <ContentTab
                            campaign={campaign}
                            setCampaign={setCampaign}
                            templates={templates}
                            updateStep={updateStep}
                            addStep={addStep}
                            deleteStep={deleteStep}
                            moveStep={moveStep}
                        />
                    )}
                    
                    {activeTab === 'journey' && (
                        <JourneyTab
                            campaign={campaign}
                            setCampaign={setCampaign}
                            workflows={workflows}
                            templates={templates}
                            updateStep={updateStep}
                            addStep={addStep}
                            deleteStep={deleteStep}
                            moveStep={moveStep}
                            onSwitchToContentTab={() => setActiveTab('content')}
                        />
                    )}
                    
                    {activeTab === 'settings' && (
                        <SettingsTab
                            campaign={campaign}
                            setCampaign={setCampaign}
                            sendingProfiles={sendingProfiles}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

// Audience Tab Component
const AudienceTab = ({ campaign, setCampaign, audiences, isEditing, id }) => {
    return (
        <div className="space-y-6">
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-foreground">Select Audience</h3>
                    <Link
                        to="/audiences/new"
                        onClick={() => {
                            // Save campaign state before navigating
                            if (campaign.name || campaign.steps.length > 0 || campaign.audienceId) {
                                sessionStorage.setItem('campaignBuilder_draft', JSON.stringify(campaign));
                                sessionStorage.setItem('campaignBuilder_returnTo', isEditing ? `/campaigns/${id}` : '/campaigns/new');
                            }
                        }}
                        state={{ returnTo: isEditing ? `/campaigns/${id}` : `/campaigns/new`, campaignId: id }}
                        className="text-sm text-primary hover:text-primary/80 flex items-center gap-1"
                    >
                        <PlusIcon className="h-4 w-4" />
                        Create New Audience
                    </Link>
                </div>
                <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                        Audience
                    </label>
                    <div className="flex gap-2">
                        <select
                            value={campaign.audienceId ? String(campaign.audienceId) : ''}
                            onChange={(e) => setCampaign(prev => ({ ...prev, audienceId: e.target.value || null }))}
                            className="flex-1 px-3 py-2 border border-input rounded-md bg-background text-foreground"
                        >
                            <option value="">Select an audience...</option>
                            {audiences.length === 0 ? (
                                <option disabled>No audiences available</option>
                            ) : (
                                audiences.map(audience => {
                                    const audienceId = audience._id || audience.id;
                                    return (
                                        <option key={audienceId} value={String(audienceId)}>
                                            {audience.name} ({audience.type === 'static' ? 'Static' : 'Segment'}) - {audience.estimatedSize || 0} contacts
                                        </option>
                                    );
                                })
                            )}
                        </select>
                    </div>
                    {campaign.audienceId && (
                        <p className="text-xs text-muted-foreground mt-1">
                            Selected: {audiences.find(a => String(a._id || a.id) === String(campaign.audienceId))?.name || 'Loading...'}
                        </p>
                    )}
                    {audiences.length === 0 && (
                        <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-md">
                            <p className="text-sm text-blue-800 dark:text-blue-200">
                                No audiences found. <Link 
                                    to="/audiences/new"
                                    onClick={() => {
                                        // Save campaign state before navigating
                                        if (campaign.name || campaign.steps.length > 0 || campaign.audienceId) {
                                            sessionStorage.setItem('campaignBuilder_draft', JSON.stringify(campaign));
                                            sessionStorage.setItem('campaignBuilder_returnTo', isEditing ? `/campaigns/${id}` : '/campaigns/new');
                                        }
                                    }}
                                    state={{ returnTo: isEditing ? `/campaigns/${id}` : `/campaigns/new`, campaignId: id }}
                                    className="underline font-medium"
                                >Create your first audience</Link> to segment your contacts.
                            </p>
                        </div>
                    )}
                </div>
            </div>
            
            <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Suppression Lists</h3>
                <div className="space-y-3">
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={campaign.suppressionLists?.globalUnsub || false}
                            onChange={(e) => setCampaign(prev => ({
                                ...prev,
                                suppressionLists: {
                                    ...prev.suppressionLists,
                                    globalUnsub: e.target.checked
                                }
                            }))}
                            className="h-4 w-4 text-primary"
                        />
                        <span className="text-sm text-foreground">Exclude globally unsubscribed contacts</span>
                    </label>
                </div>
            </div>
            
            <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Frequency Cap</h3>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Max Emails
                        </label>
                        <input
                            type="number"
                            value={campaign.frequencyCap?.max || 2}
                            onChange={(e) => setCampaign(prev => ({
                                ...prev,
                                frequencyCap: {
                                    ...prev.frequencyCap,
                                    max: parseInt(e.target.value) || 0
                                }
                            }))}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                            min="1"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Per (days)
                        </label>
                        <input
                            type="number"
                            value={campaign.frequencyCap?.windowDays || 7}
                            onChange={(e) => setCampaign(prev => ({
                                ...prev,
                                frequencyCap: {
                                    ...prev.frequencyCap,
                                    windowDays: parseInt(e.target.value) || 0
                                }
                            }))}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                            min="1"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

// Content Tab Component
const ContentTab = ({ campaign, setCampaign, templates, updateStep, addStep, deleteStep, moveStep }) => {
    const sendSteps = campaign.steps.filter(s => s.kind === 'send');
    
    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">Email Content</h3>
                {campaign.type === 'broadcast' && (
                    <button
                        onClick={() => addStep('send')}
                        className="px-3 py-1.5 bg-primary text-white rounded-md hover:bg-primary/90 flex items-center gap-2 text-sm"
                    >
                        <PlusIcon className="h-4 w-4" />
                        Add Email
                    </button>
                )}
            </div>
            
            {sendSteps.length === 0 ? (
                <div className="border-2 border-dashed border-input rounded-lg p-12 text-center">
                    <DocumentTextIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">No email content added yet</p>
                    <button
                        onClick={() => addStep('send')}
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                    >
                        Add First Email
                    </button>
                </div>
            ) : (
                <div className="space-y-4">
                    {sendSteps.map((step, index) => {
                        const journeyPosition = campaign.steps.findIndex(s => s.id === step.id) + 1;
                        return (
                            <EmailStepEditor
                                key={step.id}
                                step={step}
                                stepIndex={index}
                                journeyPosition={journeyPosition}
                                templates={templates}
                                updateStep={updateStep}
                                deleteStep={deleteStep}
                                moveStep={moveStep}
                                canMoveUp={index > 0}
                                canMoveDown={index < sendSteps.length - 1}
                                allSteps={campaign.steps}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// Email Step Editor Component
const EmailStepEditor = ({ step, stepIndex, journeyPosition, templates, updateStep, deleteStep, moveStep, canMoveUp, canMoveDown, allSteps }) => {
    // Normalize templateId to string when initializing variant
    const normalizeVariant = (variant) => {
        if (!variant) return {};
        const normalized = { ...variant };
        if (normalized.templateId) {
            if (typeof normalized.templateId === 'object') {
                normalized.templateId = normalized.templateId._id || normalized.templateId.id || String(normalized.templateId);
            } else {
                normalized.templateId = String(normalized.templateId);
            }
        }
        return normalized;
    };
    
    const [variant, setVariant] = useState(normalizeVariant(step.variants?.[0]));
    
    useEffect(() => {
        if (step.variants?.[0]) {
            setVariant(normalizeVariant(step.variants[0]));
        }
    }, [step]);
    
    const handleVariantChange = (field, value) => {
        const updatedVariant = { ...variant, [field]: value };
        setVariant(updatedVariant);
        updateStep(step.id, {
            variants: [updatedVariant]
        });
    };
    
    return (
        <div className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">Email {stepIndex + 1}</span>
                    {step.name && (
                        <span className="text-sm text-muted-foreground">- {step.name}</span>
                    )}
                    {journeyPosition && (
                        <span 
                            className="text-xs text-muted-foreground font-mono bg-muted px-2 py-1 rounded opacity-60 hover:opacity-100 transition-opacity"
                            title={`Step ID: ${step.id} | Journey Position: Step ${journeyPosition}`}
                        >
                            Journey: Step {journeyPosition}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {canMoveUp && (
                        <button
                            onClick={() => moveStep(step.id, 'up')}
                            className="p-1 text-muted-foreground hover:text-foreground"
                            title="Move up"
                        >
                            <ArrowUpIcon className="h-4 w-4" />
                        </button>
                    )}
                    {canMoveDown && (
                        <button
                            onClick={() => moveStep(step.id, 'down')}
                            className="p-1 text-muted-foreground hover:text-foreground"
                            title="Move down"
                        >
                            <ArrowDownIcon className="h-4 w-4" />
                        </button>
                    )}
                    <button
                        onClick={() => deleteStep(step.id)}
                        className="p-1 text-red-600 hover:text-red-800"
                        title="Delete"
                    >
                        <TrashIcon className="h-4 w-4" />
                    </button>
                </div>
            </div>
            
            <div className="space-y-5">
                <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                        Email Template *
                    </label>
                    <select
                        value={variant.templateId || ''}
                        onChange={(e) => handleVariantChange('templateId', e.target.value)}
                        className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
                    >
                        <option value="">Select a template...</option>
                        {templates.length === 0 ? (
                            <option disabled>No templates available</option>
                        ) : (
                            templates.map(template => (
                                <option key={template._id} value={template._id}>
                                    {template.name}
                                </option>
                            ))
                        )}
                    </select>
                    {templates.length === 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                            <Link to="/email-templates" className="text-primary hover:underline">Create email templates</Link> first
                        </p>
                    )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                            Subject Line Override
                        </label>
                        <input
                            type="text"
                            value={variant.subject || ''}
                            onChange={(e) => handleVariantChange('subject', e.target.value)}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
                            placeholder="Leave empty to use template subject"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                            Optional: Override the template's default subject line
                        </p>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                            Preheader Text
                        </label>
                        <input
                            type="text"
                            value={variant.preheader || ''}
                            onChange={(e) => handleVariantChange('preheader', e.target.value)}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
                            placeholder="Preview text shown in email clients"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                            Short preview text shown next to subject in inbox
                        </p>
                    </div>
                </div>
                
                <div className="border-t border-border pt-4">
                    <label className="block text-sm font-medium text-foreground mb-3">
                        Tracking Settings
                    </label>
                    <div className="space-y-3">
                        <label className="flex items-center gap-3 p-3 border border-input rounded-md hover:bg-muted/50 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={step.tracking?.pixel || false}
                                onChange={(e) => updateStep(step.id, {
                                    tracking: { ...step.tracking, pixel: e.target.checked }
                                })}
                                className="h-4 w-4 text-primary"
                            />
                            <div>
                                <span className="text-sm font-medium text-foreground">Track email opens</span>
                                <p className="text-xs text-muted-foreground">Track when recipients open this email</p>
                            </div>
                        </label>
                        <label className="flex items-center gap-3 p-3 border border-input rounded-md hover:bg-muted/50 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={step.tracking?.links || false}
                                onChange={(e) => updateStep(step.id, {
                                    tracking: { ...step.tracking, links: e.target.checked }
                                })}
                                className="h-4 w-4 text-primary"
                            />
                            <div>
                                <span className="text-sm font-medium text-foreground">Track link clicks</span>
                                <p className="text-xs text-muted-foreground">Track which links recipients click</p>
                            </div>
                        </label>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Journey Tab Component
const JourneyTab = ({ campaign, setCampaign, workflows, templates, updateStep, addStep, deleteStep, moveStep, onSwitchToContentTab }) => {
    // Journey-specific content (step builder)
    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Entry Trigger</h3>
                {campaign.type === 'journey' && (
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                            Workflow Trigger
                        </label>
                        <select
                            value={campaign.entryRule?.workflowTriggerId || ''}
                            onChange={(e) => setCampaign(prev => ({
                                ...prev,
                                entryRule: {
                                    ...prev.entryRule,
                                    workflowTriggerId: e.target.value || null
                                }
                            }))}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                        >
                            <option value="">Select a workflow trigger...</option>
                            {workflows.map(workflow => (
                                <option key={workflow._id} value={workflow._id}>
                                    {workflow.name} ({workflow.trigger?.event || 'No trigger'})
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-muted-foreground mt-1">
                            This campaign will start when the selected workflow trigger fires
                        </p>
                    </div>
                )}
            </div>
            
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-foreground">Journey Steps</h3>
                    <div className="flex gap-2">
                        <button
                            onClick={() => addStep('send')}
                            className="px-3 py-1.5 border border-input rounded-md hover:bg-muted text-sm flex items-center gap-2"
                        >
                            <PlusIcon className="h-4 w-4" />
                            Send Email
                        </button>
                        <button
                            onClick={() => addStep('wait')}
                            className="px-3 py-1.5 border border-input rounded-md hover:bg-muted text-sm flex items-center gap-2"
                        >
                            <PlusIcon className="h-4 w-4" />
                            Wait
                        </button>
                        <button
                            onClick={() => addStep('branch')}
                            className="px-3 py-1.5 border border-input rounded-md hover:bg-muted text-sm flex items-center gap-2"
                        >
                            <PlusIcon className="h-4 w-4" />
                            Branch
                        </button>
                        <button
                            onClick={() => addStep('goal')}
                            className="px-3 py-1.5 border border-input rounded-md hover:bg-muted text-sm flex items-center gap-2"
                        >
                            <PlusIcon className="h-4 w-4" />
                            Goal
                        </button>
                    </div>
                </div>
                
                {campaign.steps.length === 0 ? (
                    <div className="border-2 border-dashed border-input rounded-lg p-12 text-center">
                        <SparklesIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground mb-4">No steps added yet</p>
                        <p className="text-sm text-muted-foreground">Add steps to build your journey</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {campaign.steps.map((step, index) => (
                            <JourneyStepEditor
                                key={step.id}
                                step={step}
                                stepIndex={index}
                                templates={templates}
                                updateStep={updateStep}
                                deleteStep={deleteStep}
                                moveStep={moveStep}
                                canMoveUp={index > 0}
                                canMoveDown={index < campaign.steps.length - 1}
                                allSteps={campaign.steps}
                                onSwitchToContentTab={onSwitchToContentTab}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// Journey Step Editor Component (simplified - full implementation would handle all step types)
const JourneyStepEditor = ({ step, stepIndex, templates, updateStep, deleteStep, moveStep, canMoveUp, canMoveDown, allSteps, onSwitchToContentTab }) => {
    const getStepIcon = (kind) => {
        const icons = {
            'send': '📧',
            'wait': '⏱️',
            'branch': '🔀',
            'goal': '🎯',
            'exit': '🚪'
        };
        return icons[kind] || '📌';
    };
    
    const getStepColor = (kind) => {
        const colors = {
            'send': 'bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300',
            'wait': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-300',
            'branch': 'bg-purple-100 text-purple-700 dark:bg-purple-950/30 dark:text-purple-300',
            'goal': 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300',
            'exit': 'bg-gray-100 text-gray-700 dark:bg-gray-950/30 dark:text-gray-300'
        };
        return colors[kind] || 'bg-gray-100 text-gray-700';
    };
    
    return (
        <div className="border border-border rounded-lg p-6 bg-background hover:border-primary/50 transition-colors">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-lg ${getStepColor(step.kind)} flex items-center justify-center text-2xl`}>
                        {getStepIcon(step.kind)}
                    </div>
                    <div>
                        <div className="font-semibold text-foreground capitalize">{step.kind} Step</div>
                        {step.name ? (
                            <div className="text-sm text-muted-foreground">{step.name}</div>
                        ) : (
                            <div className="text-sm text-muted-foreground">Step {stepIndex + 1}</div>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {canMoveUp && (
                        <button 
                            onClick={() => moveStep(step.id, 'up')} 
                            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
                            title="Move up"
                        >
                            <ArrowUpIcon className="h-4 w-4" />
                        </button>
                    )}
                    {canMoveDown && (
                        <button 
                            onClick={() => moveStep(step.id, 'down')} 
                            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
                            title="Move down"
                        >
                            <ArrowDownIcon className="h-4 w-4" />
                        </button>
                    )}
                    <button 
                        onClick={() => deleteStep(step.id)} 
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-md transition-colors"
                        title="Delete"
                    >
                        <TrashIcon className="h-4 w-4" />
                    </button>
                </div>
            </div>
            
            {/* Step-specific configuration would go here */}
            {step.kind === 'send' && (
                <div className="space-y-3">
                    {/* Show which email this is in Content tab */}
                    {(() => {
                        const sendSteps = allSteps.filter(s => s.kind === 'send');
                        const emailIndex = sendSteps.findIndex(s => s.id === step.id);
                        const emailNumber = emailIndex >= 0 ? emailIndex + 1 : null;
                        const variant = step.variants?.[0];
                        // Normalize templateId to string for comparison
                        let templateIdStr = null;
                        if (variant?.templateId) {
                            if (typeof variant.templateId === 'object') {
                                templateIdStr = variant.templateId._id || variant.templateId.id || String(variant.templateId);
                            } else {
                                templateIdStr = String(variant.templateId);
                            }
                        }
                        const template = templateIdStr ? templates.find(t => String(t._id) === templateIdStr) : null;
                        
                        return (
                            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-sm font-medium text-foreground">
                                            {emailNumber ? `Email ${emailNumber}` : 'Email Step'} in Content Tab
                                        </div>
                                        {step.name && (
                                            <div className="text-xs text-muted-foreground mt-1">
                                                Step Name: {step.name}
                                            </div>
                                        )}
                                        <div className="text-xs text-muted-foreground mt-1 opacity-60" title={`Step ID: ${step.id}`}>
                                            Journey Position: Step {stepIndex + 1}
                                        </div>
                                    </div>
                                </div>
                                
                                {template ? (
                                    <div className="border-t border-border pt-3 space-y-2">
                                        <div>
                                            <span className="text-xs font-medium text-muted-foreground">Template:</span>
                                            <div className="text-sm text-foreground font-medium mt-1">
                                                {template.name}
                                            </div>
                                        </div>
                                        {variant?.subject && (
                                            <div>
                                                <span className="text-xs font-medium text-muted-foreground">Subject Override:</span>
                                                <div className="text-sm text-foreground mt-1">
                                                    {variant.subject}
                                                </div>
                                            </div>
                                        )}
                                        {variant?.preheader && (
                                            <div>
                                                <span className="text-xs font-medium text-muted-foreground">Preheader:</span>
                                                <div className="text-sm text-foreground mt-1">
                                                    {variant.preheader}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="border-t border-border pt-3">
                                        <div className="text-sm text-muted-foreground">
                                            ⚠️ No email template selected
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1">
                                            Go to Content tab to configure this email
                                        </div>
                                    </div>
                                )}
                                
                                <div className="border-t border-border pt-3">
                                    {onSwitchToContentTab && (
                                        <button
                                            onClick={onSwitchToContentTab}
                                            className="text-sm text-primary hover:text-primary/80 font-medium flex items-center gap-1"
                                        >
                                            <DocumentTextIcon className="h-4 w-4" />
                                            Configure in Content Tab
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })()}
                </div>
            )}
            {step.kind === 'wait' && (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                            Wait Type
                        </label>
                        <select
                            value={step.waitRule?.type || 'duration'}
                            onChange={(e) => updateStep(step.id, {
                                waitRule: {
                                    ...step.waitRule,
                                    type: e.target.value,
                                    value: e.target.value === 'after_event' ? 'reply' : (step.waitRule?.value || 'P1D')
                                }
                            })}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                        >
                            <option value="duration">Fixed Duration</option>
                            <option value="until">Until Specific Time</option>
                            <option value="after_event">Wait for Event (Auto-bump)</option>
                        </select>
                    </div>
                    
                    {step.waitRule?.type === 'after_event' ? (
                        <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-md">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Wait For
                                </label>
                                <select
                                    value={step.waitRule?.value || 'reply'}
                                    onChange={(e) => updateStep(step.id, {
                                        waitRule: { ...step.waitRule, value: e.target.value }
                                    })}
                                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                                >
                                    <option value="reply">Email Reply</option>
                                </select>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Wait for recipient to reply. If no reply after the duration, send bump email.
                                </p>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Wait Duration (before sending bump)
                                </label>
                                <input
                                    type="text"
                                    value={step.waitRule?.waitDuration || 'P3D'}
                                    onChange={(e) => updateStep(step.id, {
                                        waitRule: { ...step.waitRule, waitDuration: e.target.value }
                                    })}
                                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                                    placeholder="e.g., P3D (3 days), 72 hours"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    How long to wait before sending the bump email if no reply
                                </p>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Bump Email Step
                                </label>
                                <select
                                    value={step.bumpStepId || ''}
                                    onChange={(e) => updateStep(step.id, {
                                        bumpStepId: e.target.value || null
                                    })}
                                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                                >
                                    <option value="">Select bump email step...</option>
                                    {allSteps
                                        .filter(s => s.kind === 'send' && s.id !== step.id)
                                        .map(sendStep => (
                                            <option key={sendStep.id} value={sendStep.id}>
                                                {sendStep.name || `Email Step ${allSteps.indexOf(sendStep) + 1}`}
                                            </option>
                                        ))}
                                </select>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Select the email step to send if recipient doesn't reply
                                </p>
                            </div>
                            
                            <div className="p-3 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                                <p className="text-xs text-yellow-800 dark:text-yellow-200">
                                    💡 <strong>Auto-bump:</strong> If recipient replies, the sequence will automatically stop. If no reply after the wait duration, the selected bump email will be sent.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                {step.waitRule?.type === 'until' ? 'Wait Until' : 'Wait Duration'}
                            </label>
                            <input
                                type={step.waitRule?.type === 'until' ? 'datetime-local' : 'text'}
                                value={step.waitRule?.value || ''}
                                onChange={(e) => updateStep(step.id, {
                                    waitRule: { ...step.waitRule, value: e.target.value }
                                })}
                                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                                placeholder={step.waitRule?.type === 'until' ? 'Select date and time' : 'e.g., P2D (2 days), 48 hours'}
                            />
                        </div>
                    )}
                </div>
            )}
            
            {step.kind === 'branch' && (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                            Condition
                        </label>
                        <select
                            value={step.condition?.type || 'opened'}
                            onChange={(e) => updateStep(step.id, {
                                condition: {
                                    ...step.condition,
                                    type: e.target.value
                                }
                            })}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                        >
                            <option value="opened">Email Opened</option>
                            <option value="clicked">Link Clicked</option>
                            <option value="replied">Email Replied</option>
                            <option value="goal_met">Goal Met</option>
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1">
                                If True → Step
                            </label>
                            <select
                                value={step.thenId || ''}
                                onChange={(e) => updateStep(step.id, { thenId: e.target.value || null })}
                                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                            >
                                <option value="">Select step...</option>
                                {allSteps
                                    .filter(s => s.id !== step.id)
                                    .map(s => (
                                        <option key={s.id} value={s.id}>
                                            {s.name || `${s.kind} Step ${allSteps.indexOf(s) + 1}`}
                                        </option>
                                    ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-1">
                                If False → Step
                            </label>
                            <select
                                value={step.elseId || ''}
                                onChange={(e) => updateStep(step.id, { elseId: e.target.value || null })}
                                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                            >
                                <option value="">Select step...</option>
                                {allSteps
                                    .filter(s => s.id !== step.id)
                                    .map(s => (
                                        <option key={s.id} value={s.id}>
                                            {s.name || `${s.kind} Step ${allSteps.indexOf(s) + 1}`}
                                        </option>
                                    ))}
                            </select>
                        </div>
                    </div>
                </div>
            )}
            
            {step.kind === 'goal' && (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                            Goal Event
                        </label>
                        <select
                            value={step.goal?.event || 'order_placed'}
                            onChange={(e) => updateStep(step.id, {
                                goal: { ...step.goal, event: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                        >
                            <option value="order_placed">Order Placed</option>
                            <option value="lead_stage_changed">Lead Stage Changed</option>
                            <option value="form_submitted">Form Submitted</option>
                            <option value="link_clicked">Link Clicked</option>
                        </select>
                    </div>
                    <div>
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={step.goal?.exitOnHit || false}
                                onChange={(e) => updateStep(step.id, {
                                    goal: { ...step.goal, exitOnHit: e.target.checked }
                                })}
                                className="h-4 w-4 text-primary"
                            />
                            <span className="text-sm text-foreground">Exit sequence when goal is met</span>
                        </label>
                    </div>
                </div>
            )}
        </div>
    );
};

// Settings Tab Component
const SettingsTab = ({ campaign, setCampaign, sendingProfiles }) => {
    const [verifying, setVerifying] = useState(false);
    
    // Find the default sending profile
    const defaultProfile = sendingProfiles.find(p => p.isDefault);
    const selectedProfile = campaign.sendingProfileId 
        ? sendingProfiles.find(p => p._id === campaign.sendingProfileId)
        : defaultProfile;
    
    // Check if profile is verified
    // For provider-based profiles, they're considered verified (provider handles it)
    // For custom domains, check verification status
    const isProviderBased = selectedProfile?.provider && selectedProfile.provider !== 'custom';
    const isVerified = selectedProfile && (
        isProviderBased || // Provider-based profiles are considered verified
        (selectedProfile.verification?.domain?.status === 'verified' &&
         (!selectedProfile.dkim?.enabled || selectedProfile.verification?.dkim?.status === 'verified') &&
         (!selectedProfile.spf?.enabled || selectedProfile.verification?.spf?.status === 'verified'))
    );
    
    const handleVerify = async () => {
        if (!selectedProfile) return;
        
        try {
            setVerifying(true);
            // Verify domain first
            const domainRes = await sendingProfilesAPI.verify(selectedProfile._id, { type: 'domain' });
            
            // If DKIM is enabled, verify it
            if (selectedProfile.dkim?.enabled) {
                await sendingProfilesAPI.verify(selectedProfile._id, { type: 'dkim' });
            }
            
            // If SPF is enabled, verify it
            if (selectedProfile.spf?.enabled) {
                await sendingProfilesAPI.verify(selectedProfile._id, { type: 'spf' });
            }
            
            toast.success('Sending profile verified successfully');
            
            // Reload sending profiles to get updated verification status
            const profilesRes = await sendingProfilesAPI.getAll({ limit: 100 });
            if (profilesRes.data.success) {
                // Update the sending profiles in parent component
                // For now, we'll reload the page to get fresh data
                window.location.reload();
            }
        } catch (error) {
            console.error('Failed to verify sending profile:', error);
            toast.error(error.response?.data?.message || 'Failed to verify sending profile');
        } finally {
            setVerifying(false);
        }
    };
    
    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Sending Profile</h3>
                <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                        Sending Profile
                    </label>
                    <div className="flex gap-2">
                        <select
                            value={campaign.sendingProfileId || ''}
                            onChange={(e) => setCampaign(prev => ({ ...prev, sendingProfileId: e.target.value || null }))}
                            className="flex-1 px-3 py-2 border border-input rounded-md bg-background text-foreground"
                        >
                            <option value="">Use default sending profile</option>
                            {sendingProfiles.map(profile => (
                                <option key={profile._id} value={profile._id}>
                                    {profile.name} ({profile.domain})
                                </option>
                            ))}
                        </select>
                        <Link
                            to="/sending-profiles"
                            className="px-3 py-2 border border-input rounded-md hover:bg-muted text-sm whitespace-nowrap"
                        >
                            Manage Profiles
                        </Link>
                    </div>
                    
                    {/* Show verification status */}
                    {selectedProfile && (
                        <div className="mt-3 p-3 rounded-md border" style={{
                            backgroundColor: isVerified ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                            borderColor: isVerified ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'
                        }}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    {isVerified ? (
                                        <CheckCircleIcon className="h-5 w-5 text-green-600" />
                                    ) : (
                                        <XCircleIcon className="h-5 w-5 text-red-600" />
                                    )}
                                    <span className="text-sm font-medium" style={{
                                        color: isVerified ? 'rgb(22, 163, 74)' : 'rgb(220, 38, 38)'
                                    }}>
                                        {isProviderBased 
                                            ? `"${selectedProfile.name || selectedProfile.domain || 'Provider'}" (${selectedProfile.provider === 'sendgrid' ? 'SendGrid' : 
                                                                                                                      selectedProfile.provider === 'mailgun' ? 'Mailgun' :
                                                                                                                      selectedProfile.provider === 'ses' ? 'AWS SES' :
                                                                                                                      selectedProfile.provider === 'postmark' ? 'Postmark' :
                                                                                                                      selectedProfile.provider === 'mailchimp' ? 'Mailchimp' : 'Provider'})`
                                            : isVerified 
                                                ? `"${selectedProfile.name || selectedProfile.domain}" is verified`
                                                : `"${selectedProfile.name || selectedProfile.domain}" is not verified`
                                        }
                                    </span>
                                </div>
                                {!isVerified && !isProviderBased && (
                                    <button
                                        onClick={handleVerify}
                                        disabled={verifying}
                                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        {verifying ? 'Verifying...' : 'Verify Now'}
                                    </button>
                                )}
                            </div>
                            {!isVerified && !isProviderBased && (
                                <p className="text-xs mt-2" style={{ color: 'rgb(185, 28, 28)' }}>
                                    You must verify this sending profile before starting a campaign. Click "Verify Now" to verify.
                                </p>
                            )}
                            {isProviderBased && (
                                <p className="text-xs mt-2" style={{ color: 'rgb(22, 163, 74)' }}>
                                    Email service provider profiles are automatically verified by the provider. No manual verification needed.
                                </p>
                            )}
                        </div>
                    )}
                    
                    {!selectedProfile && campaign.sendingProfileId === null && sendingProfiles.length === 0 && (
                        <div className="mt-3 p-3 rounded-md border border-blue-300 bg-blue-50">
                            <p className="text-sm text-blue-800">
                                A system default sending profile will be created automatically when you start your campaign. You can also create a custom profile in Settings &gt; Sending Profiles.
                            </p>
                        </div>
                    )}
                </div>
            </div>
            
            <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">UTM Parameters</h3>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Source</label>
                        <input
                            type="text"
                            value={campaign.utmParams?.source || ''}
                            onChange={(e) => setCampaign(prev => ({
                                ...prev,
                                utmParams: { ...prev.utmParams, source: e.target.value }
                            }))}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                            placeholder="newsletter"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Medium</label>
                        <input
                            type="text"
                            value={campaign.utmParams?.medium || 'email'}
                            onChange={(e) => setCampaign(prev => ({
                                ...prev,
                                utmParams: { ...prev.utmParams, medium: e.target.value }
                            }))}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Campaign</label>
                        <input
                            type="text"
                            value={campaign.utmParams?.campaign || ''}
                            onChange={(e) => setCampaign(prev => ({
                                ...prev,
                                utmParams: { ...prev.utmParams, campaign: e.target.value }
                            }))}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                            placeholder={campaign.name || 'campaign-name'}
                        />
                    </div>
                </div>
            </div>
            
            <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Campaign Settings</h3>
                <div className="space-y-3">
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={campaign.settings?.trackOpens || false}
                            onChange={(e) => setCampaign(prev => ({
                                ...prev,
                                settings: { ...prev.settings, trackOpens: e.target.checked }
                            }))}
                            className="h-4 w-4 text-primary"
                        />
                        <span className="text-sm text-foreground">Track email opens</span>
                    </label>
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={campaign.settings?.trackClicks || false}
                            onChange={(e) => setCampaign(prev => ({
                                ...prev,
                                settings: { ...prev.settings, trackClicks: e.target.checked }
                            }))}
                            className="h-4 w-4 text-primary"
                        />
                        <span className="text-sm text-foreground">Track link clicks</span>
                    </label>
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={campaign.settings?.allowUnsubscribe || false}
                            onChange={(e) => setCampaign(prev => ({
                                ...prev,
                                settings: { ...prev.settings, allowUnsubscribe: e.target.checked }
                            }))}
                            className="h-4 w-4 text-primary"
                        />
                        <span className="text-sm text-foreground">Allow unsubscribe</span>
                    </label>
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={campaign.settings?.linkTracking || false}
                            onChange={(e) => setCampaign(prev => ({
                                ...prev,
                                settings: { ...prev.settings, linkTracking: e.target.checked }
                            }))}
                            className="h-4 w-4 text-primary"
                        />
                        <span className="text-sm text-foreground">Enable link tracking</span>
                    </label>
                </div>
            </div>
            
            <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Schedule</h3>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Scheduled Date & Time
                        </label>
                        <input
                            type="datetime-local"
                            value={campaign.scheduledAt ? new Date(campaign.scheduledAt).toISOString().slice(0, 16) : ''}
                            onChange={(e) => setCampaign(prev => ({
                                ...prev,
                                scheduledAt: e.target.value ? new Date(e.target.value).toISOString() : null
                            }))}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                            {campaign.type === 'broadcast' 
                                ? 'Set when to send this broadcast campaign'
                                : campaign.type === 'sequence'
                                ? 'Set when to start this email sequence'
                                : 'Set when to start this journey'}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Timezone
                        </label>
                        <select
                            value={campaign.timezone || 'Europe/Madrid'}
                            onChange={(e) => setCampaign(prev => ({ ...prev, timezone: e.target.value }))}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                        >
                            <option value="Europe/Madrid">Europe/Madrid</option>
                            <option value="UTC">UTC</option>
                            <option value="America/New_York">America/New_York</option>
                            <option value="America/Los_Angeles">America/Los_Angeles</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CampaignBuilder;

